import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { ReactComponent as MergeIcon } from '../../../../assets/20x20/merge.svg';
import useSelectedEnv from '../../../../hooks/useSelectedEnv';
import useGraphqlQuery from '../../../../hooks/useGraphqlQuery';
import { MERGE_INCIDENT_IDS_PARAM } from '../../../../constants/urlParams';
import { AGENT_MERGE_INCIDENT_TITLES } from '../../../../constants/queryKeys';
import { agentMergeIncidentsPlan, aiChatPanel } from '../../../../atoms/atoms';
import type { PlanApprovalStatus } from '../../../../types';
import type { ApprovalSubmissionResult, ToolRendererArgs } from '../../types';
import type { ApprovalToolRendererProps } from '../../components/ApprovalToolRenderer';
import { Flex, FlexColumn } from '../../../Flex';
import Button from '../../../Button';
import SvgIcon from '../../../SvgIcon';
import {
  ApprovalCard,
  ApprovalCardBody,
  ApprovalCardHeader,
  ApprovalCardTitle,
} from '../approvalCard';
import {
  neverReachedApproval,
  resolveCompletedStatus,
} from '../planApprovalStatus';
import { fontSize, fontWeight, space } from '../../../../style/utils';
import { getIncidentTitlesQuery } from './api';
import type { IncidentTitlesResponse } from './types';
import { buildNewArgsFromPlan, planFromToolArgs } from './utils';

const MERGE_PATH = '/incidents/merge';

const ViewLink = styled(Flex)`
  align-items: center;
  gap: ${space('xs')};
  font-size: ${fontSize('md')};
  color: ${({ theme }) => theme.palette.accent.primary};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const SectionLabel = styled(Typography)`
  font-size: ${fontSize('sm')};
  font-weight: ${fontWeight('semibold')};
  color: ${({ theme }) => theme.palette.content.faint};
`;

const IncidentList = styled.ul`
  margin: ${space('xs', 0, 0)};
  padding-left: ${space('lgPlus')};

  li {
    font-size: ${fontSize('md')};
    color: ${({ theme }) => theme.palette.content.tertiary};
    word-break: break-word;
  }
`;

const IncidentValue = styled(Typography)`
  font-size: ${fontSize('md')};
  color: ${({ theme }) => theme.palette.content.tertiary};
  word-break: break-word;
  margin-top: ${space('xs')};
`;

type InnerProps = {
  toolArgs: ToolRendererArgs;
  toolCallId: string;
  resultIsError?: boolean;
  didAwaitApproval?: boolean;
  onApproveToolUse: (
    approved: boolean,
    newArgs?: Record<string, unknown>,
  ) => Promise<ApprovalSubmissionResult>;
  isActive?: boolean;
  interactionsDisabled?: boolean;
};

const MergeIncidentsToolRendererInner = ({
  toolArgs,
  toolCallId,
  resultIsError,
  didAwaitApproval,
  onApproveToolUse,
  isActive,
  interactionsDisabled,
}: InnerProps) => {
  const envId = useSelectedEnv();
  const navigate = useNavigate();
  const [plan, setPlan] = useRecoilState(agentMergeIncidentsPlan);
  const setChatPanel = useSetRecoilState(aiChatPanel);
  const responseRef = useRef<PlanApprovalStatus | null>(null);
  const responseInFlightRef = useRef(false);

  useEffect(() => {
    if (!isActive) {
      return;
    }
    setPlan((prev) => {
      if (prev?.toolCallId === toolCallId) {
        return prev;
      }
      return planFromToolArgs(toolArgs, toolCallId);
    });
  }, [isActive, setPlan, toolArgs, toolCallId]);

  useEffect(() => {
    if (
      !isActive ||
      interactionsDisabled ||
      responseInFlightRef.current ||
      !plan?.userResponse ||
      plan.agentAcknowledged ||
      plan.toolCallId !== toolCallId
    ) {
      return;
    }
    const response = plan.userResponse;
    responseInFlightRef.current = true;
    void (async () => {
      const result =
        response === 'confirmed'
          ? await onApproveToolUse(true, buildNewArgsFromPlan(plan))
          : await onApproveToolUse(false);
      responseInFlightRef.current = false;
      if (result === 'delivered') {
        responseRef.current = response;
        setPlan((prev) =>
          prev?.toolCallId === toolCallId && prev.userResponse === response
            ? { ...prev, agentAcknowledged: true }
            : prev,
        );
      } else if (result === 'failed') {
        setPlan((prev) =>
          prev?.toolCallId === toolCallId && prev.userResponse === response
            ? { ...prev, userResponse: undefined }
            : prev,
        );
      }
    })();
  }, [
    isActive,
    interactionsDisabled,
    onApproveToolUse,
    plan,
    setPlan,
    toolCallId,
  ]);

  useEffect(() => {
    if (isActive || !plan || plan.toolCallId !== toolCallId) {
      return;
    }
    setPlan(null);
  }, [isActive, plan, toolCallId, setPlan]);

  const snapshotFromToolArgs = useCallback(() => {
    setPlan((prev) => {
      const base = planFromToolArgs(toolArgs, toolCallId);
      if (prev?.toolCallId === toolCallId) {
        return { ...base, ...prev };
      }
      return base;
    });
  }, [setPlan, toolArgs, toolCallId]);

  const handleReviewClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      snapshotFromToolArgs();
      setChatPanel((prev) => ({ ...prev, isFullWidth: false }));
      const primary = String(toolArgs?.primary_incident_id ?? '');
      const sources = (toolArgs?.incident_ids as string[] | undefined) ?? [];
      const allIds = [primary, ...sources.filter((id) => id !== primary)];
      const params = new URLSearchParams();
      params.set(MERGE_INCIDENT_IDS_PARAM, allIds.join(','));
      navigate(`/${envId}${MERGE_PATH}?${params.toString()}`);
    },
    [envId, navigate, setChatPanel, snapshotFromToolArgs, toolArgs],
  );

  const handleApprove = useCallback(() => {
    setPlan((prev) => {
      const base =
        prev?.toolCallId === toolCallId
          ? prev
          : planFromToolArgs(toolArgs, toolCallId);
      return { ...base, userResponse: 'confirmed' as const };
    });
  }, [setPlan, toolArgs, toolCallId]);

  const handleReject = useCallback(() => {
    setPlan((prev) => {
      const base =
        prev?.toolCallId === toolCallId
          ? prev
          : planFromToolArgs(toolArgs, toolCallId);
      return { ...base, userResponse: 'rejected' as const };
    });
  }, [setPlan, toolArgs, toolCallId]);

  const primary = String(toolArgs?.primary_incident_id ?? '');
  const sources = useMemo(
    () => (toolArgs?.incident_ids as string[] | undefined) ?? [],
    [toolArgs],
  );
  const n = sources.length + 1;

  const allIncidentIds = useMemo(() => {
    const set = new Set<string>();
    if (primary) set.add(primary);
    sources.forEach((id) => set.add(id));
    return Array.from(set);
  }, [primary, sources]);

  const { data: titlesResponse } = useGraphqlQuery<IncidentTitlesResponse>({
    queryKey: [AGENT_MERGE_INCIDENT_TITLES, allIncidentIds],
    query: getIncidentTitlesQuery,
    variables: {
      filters: { incidentIds: allIncidentIds },
      limit: allIncidentIds.length,
    },
    enabled: allIncidentIds.length > 0,
  });

  const titlesById = useMemo(() => {
    const map = new Map<string, string>();
    titlesResponse?.incidentsV2Paged?.items?.forEach((item) => {
      map.set(item.id, item.title);
    });
    return map;
  }, [titlesResponse]);

  const labelFor = useCallback(
    (id: string) => titlesById.get(id) ?? id,
    [titlesById],
  );

  const completedFromResult = resolveCompletedStatus({
    isActive,
    didAwaitApproval,
    resultIsError,
  });

  const status: PlanApprovalStatus | undefined =
    responseRef.current ??
    (plan?.agentAcknowledged && plan.toolCallId === toolCallId
      ? plan.userResponse
      : undefined) ??
    completedFromResult;

  const headerLabel = useMemo(() => {
    if (status === 'confirmed') {
      return 'Merge approved';
    }
    if (status === 'rejected') {
      return 'Merge rejected';
    }
    return `Merge ${n} incidents`;
  }, [n, status]);

  const showActions =
    isActive &&
    plan?.toolCallId === toolCallId &&
    !plan.userResponse &&
    !plan.agentAcknowledged;

  return (
    <ApprovalCard $status={status}>
      <ApprovalCardHeader $status={status}>
        <SvgIcon width={16} height={16} fillColor="icon.faint">
          <MergeIcon />
        </SvgIcon>
        <ApprovalCardTitle $status={status}>{headerLabel}</ApprovalCardTitle>
      </ApprovalCardHeader>
      <ApprovalCardBody>
        <FlexColumn sx={{ gap: space('xs') }}>
          <SectionLabel>Merge incidents:</SectionLabel>
          <IncidentList>
            {sources.map((id) => (
              <li key={id}>{labelFor(id)}</li>
            ))}
          </IncidentList>
        </FlexColumn>
        <FlexColumn sx={{ gap: space('xs') }}>
          <SectionLabel>Primary incident:</SectionLabel>
          <IncidentValue>{labelFor(primary)}</IncidentValue>
        </FlexColumn>
        <Flex sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <ViewLink onClick={handleReviewClick}>Review and merge</ViewLink>

          {showActions && (
            <Flex sx={{ gap: 1 }}>
              <Button
                variant="outlined"
                size="extraSmall"
                disabled={interactionsDisabled}
                onClick={handleReject}
              >
                Reject
              </Button>
              <Button
                variant="contained"
                size="extraSmall"
                disabled={interactionsDisabled}
                onClick={handleApprove}
              >
                Approve
              </Button>
            </Flex>
          )}
        </Flex>
      </ApprovalCardBody>
    </ApprovalCard>
  );
};

const mergeIncidentsApprovalRenderer = ({
  invocation,
  onApproveToolUse,
  isActive,
  interactionsDisabled,
}: ApprovalToolRendererProps) => {
  if (neverReachedApproval(invocation)) {
    return null;
  }
  return (
    <MergeIncidentsToolRendererInner
      toolArgs={invocation.args ?? {}}
      toolCallId={invocation.toolCallId}
      resultIsError={invocation.result?.isError}
      didAwaitApproval={invocation.didAwaitApproval}
      onApproveToolUse={onApproveToolUse}
      isActive={isActive}
      interactionsDisabled={interactionsDisabled}
    />
  );
};

export default mergeIncidentsApprovalRenderer;
